/**
 * Normalisation (engine.md §5): a RawItem becomes the content register of a message.
 *
 * The HTML is rebuilt from an allow-list with htmlparser2 (no DOM, works in a worker): anything not on
 * the list is unwrapped or dropped, every URL is resolved against the item URL and must be absolute
 * http(s). The output is still untrusted — the UI sanitises again at render (web.md) — but it never
 * contains scripts, event handlers, styles, forms, frames or non-http(s) URLs.
 */

import { Parser } from 'htmlparser2';
import type { MediaItem, RawItem, SourceFormat } from '../../contracts/sources.js';
import { htmlToText } from '../../kernel/html.js';
import { tokens } from '../../kernel/text.js';

// ── Tree ───────────────────────────────────────────────────────────────────────────────────────

interface ElementNode {
  type: 'el';
  name: string;
  attrs: Record<string, string>;
  children: HtmlNode[];
}
interface TextNode {
  type: 'text';
  text: string;
}
type HtmlNode = ElementNode | TextNode;

function parseTree(html: string): HtmlNode[] {
  const root: ElementNode = { type: 'el', name: '#root', attrs: {}, children: [] };
  const stack: ElementNode[] = [root];
  const parser = new Parser(
    {
      onopentag(name, attribs) {
        const el: ElementNode = { type: 'el', name, attrs: attribs, children: [] };
        (stack[stack.length - 1] as ElementNode).children.push(el);
        stack.push(el);
      },
      onclosetag() {
        if (stack.length > 1) stack.pop();
      },
      ontext(text) {
        const parent = stack[stack.length - 1] as ElementNode;
        const last = parent.children[parent.children.length - 1];
        if (last && last.type === 'text') last.text += text;
        else parent.children.push({ type: 'text', text });
      },
    },
    { decodeEntities: true, lowerCaseTags: true, lowerCaseAttributeNames: true, recognizeSelfClosing: true },
  );
  parser.write(html);
  parser.end();
  return root.children;
}

// ── Element classes (engine.md §5) ─────────────────────────────────────────────────────────────

/** Removed together with everything inside them. */
const DROP_WITH_CONTENT = new Set([
  'script', 'style', 'noscript', 'template', 'svg', 'math', 'form', 'input', 'button', 'select', 'textarea', 'object', 'embed',
  // document furniture whose text is never body text
  'head', 'title', 'meta', 'link', 'base', 'option', 'optgroup', 'datalist', 'canvas', 'map', 'param', 'source', 'track', 'frameset', 'frame',
]);
/** Removed from the HTML; with keep_videos they become MediaItems. */
const MEDIA = new Set(['iframe', 'video', 'audio']);
/** Inline elements kept as they are (original format). */
const INLINE = new Set(['a', 'em', 'strong', 'b', 'i', 'u', 's', 'sub', 'sup', 'code', 'br', 'img']);
/** Blocks that hold inline content. */
const TEXT_BLOCKS = new Set(['p', 'h2', 'h3', 'h4', 'h5', 'h6', 'pre', 'figcaption']);
/** Blocks that may hold other blocks. */
const CONTAINERS = new Set(['blockquote', 'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'figure']);
/** Unwrapped into paragraphs: a boundary at the top level, transparent inside a kept block. */
const PARAGRAPH_BREAKS = new Set([
  'div', 'section', 'article', 'header', 'footer', 'aside', 'nav', 'main', 'address', 'details', 'summary', 'dl', 'dt', 'dd',
  'center', 'body', 'html', 'hgroup', 'caption', 'tfoot', 'colgroup', 'col', 'fieldset', 'legend', 'menu', 'picture',
]);
const VOID = new Set(['br', 'img', 'hr']);
/** Minimum rendered dimension of a kept image; at or below = tracking pixel (engine.md §5). */
const TRACKING_PIXEL_MAX_PX = 2;

// ── Escaping and URLs ──────────────────────────────────────────────────────────────────────────

export function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/"/g, '&quot;');
}

/** Resolve `value` against `base`; absolute http(s) only, otherwise null. */
export function resolveHttpUrl(value: string | undefined | null, base: string | null): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  let url: URL;
  try {
    url = base ? new URL(trimmed, base) : new URL(trimmed);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  return url.href;
}

function digits(value: string | undefined): string | null {
  if (value === undefined) return null;
  const m = /^\s*(\d{1,6})\s*(px)?\s*$/i.exec(value);
  return m ? (m[1] as string) : null;
}

// ── The rebuild ────────────────────────────────────────────────────────────────────────────────

export interface SanitiseOptions {
  /** The item URL relative URLs resolve against (null = relative URLs are dropped). */
  baseUrl: string | null;
  format: SourceFormat;
  keepImages: boolean;
  keepVideos: boolean;
}

export interface SanitisedHtml {
  /** Top-level blocks, in order; the body is their concatenation. */
  blocks: string[];
  /** iframe / video / audio found in the HTML (only when keepVideos). */
  media: MediaItem[];
  /** The first kept img[src]. */
  firstImage: string | null;
}

interface RenderState {
  opts: SanitiseOptions;
  plain: boolean;
  media: MediaItem[];
  firstImage: string | null;
}

function mediaFrom(el: ElementNode, s: RenderState): void {
  if (!s.opts.keepVideos) return;
  let src = el.attrs.src;
  if (!src) {
    const source = el.children.find((c): c is ElementNode => c.type === 'el' && c.name === 'source' && !!c.attrs.src);
    src = source?.attrs.src;
  }
  const url = resolveHttpUrl(src, s.opts.baseUrl);
  if (!url || s.media.some((m) => m.url === url)) return;
  s.media.push({ type: el.name === 'audio' ? 'audio' : 'video', url });
}

/** How an element is treated under the current options. */
function classify(name: string, s: RenderState): 'drop' | 'media' | 'inline' | 'block' | 'break' | 'hr' | 'unwrap' {
  if (DROP_WITH_CONTENT.has(name)) return 'drop';
  if (MEDIA.has(name)) return 'media';
  if (!s.opts.keepImages && (name === 'img' || name === 'figure')) return 'drop';
  if (s.plain) {
    if (name === 'a' || name === 'br') return 'inline';
    if (name === 'img') return 'drop';
    if (TEXT_BLOCKS.has(name) || CONTAINERS.has(name) || PARAGRAPH_BREAKS.has(name) || name === 'h1' || name === 'hr') return 'break';
    return 'unwrap';
  }
  if (name === 'hr') return 'hr';
  if (INLINE.has(name)) return 'inline';
  if (TEXT_BLOCKS.has(name) || CONTAINERS.has(name) || name === 'h1') return 'block';
  if (PARAGRAPH_BREAKS.has(name)) return 'break';
  return 'unwrap';
}

/** Allowed attributes of a kept element, or null when the element itself must go (img without src). */
function attributes(el: ElementNode, s: RenderState): string | null {
  const a = el.attrs;
  switch (el.name) {
    case 'a': {
      const href = resolveHttpUrl(a.href, s.opts.baseUrl);
      return href ? ` href="${escapeAttr(href)}"` : null;
    }
    case 'img': {
      const src = resolveHttpUrl(a.src, s.opts.baseUrl);
      if (!src) return null;
      const width = digits(a.width);
      const height = digits(a.height);
      if ((width !== null && Number(width) <= TRACKING_PIXEL_MAX_PX) || (height !== null && Number(height) <= TRACKING_PIXEL_MAX_PX)) return null;
      s.firstImage ??= src;
      let out = ` src="${escapeAttr(src)}"`;
      if (a.alt !== undefined) out += ` alt="${escapeAttr(a.alt)}"`;
      if (width !== null) out += ` width="${width}"`;
      if (height !== null) out += ` height="${height}"`;
      return out;
    }
    case 'td':
    case 'th': {
      let out = '';
      const colspan = digits(a.colspan);
      const rowspan = digits(a.rowspan);
      if (colspan !== null) out += ` colspan="${colspan}"`;
      if (rowspan !== null) out += ` rowspan="${rowspan}"`;
      return out;
    }
    case 'p': {
      const t = a['data-t'];
      return t !== undefined && /^\d{1,7}(\.\d{1,3})?$/.test(t.trim()) ? ` data-t="${t.trim()}"` : '';
    }
    default:
      return '';
  }
}

/** True when rendered markup has no visible content (only whitespace and line breaks). */
function isBlank(inner: string): boolean {
  return !/<img\b/.test(inner) && inner.replace(/<br>/g, '').replace(/<[^>]+>/g, '').trim() === '';
}

/** Render a kept element (and its subtree) inside a kept context. Returns '' when it disappears. */
function renderElement(el: ElementNode, s: RenderState): string {
  const kind = classify(el.name, s);
  switch (kind) {
    case 'drop':
      return '';
    case 'media':
      mediaFrom(el, s);
      return '';
    case 'break':
    case 'unwrap':
      return renderChildren(el.children, s);
    case 'hr':
      return '<hr>';
    default:
      break;
  }
  const name = el.name === 'h1' ? 'h2' : el.name;
  const attrs = attributes(el, s);
  if (attrs === null) return el.name === 'a' ? renderChildren(el.children, s) : '';
  if (VOID.has(name)) return `<${name}${attrs}>`;
  const inner = renderChildren(el.children, s);
  if (name !== 'td' && name !== 'th' && isBlank(inner) && name !== 'pre') return '';
  return `<${name}${attrs}>${inner}</${name}>`;
}

function renderChildren(children: readonly HtmlNode[], s: RenderState): string {
  let out = '';
  for (const c of children) out += c.type === 'text' ? escapeHtml(c.text) : renderElement(c, s);
  return out;
}

/** Top-level flow: inline content gathers into implicit paragraphs, blocks stand alone. */
class Flow {
  readonly blocks: string[] = [];
  private inline = '';

  addInline(html: string): void {
    this.inline += html;
  }

  addBlock(html: string): void {
    this.flush();
    if (html !== '') this.blocks.push(html);
  }

  flush(): void {
    const inner = this.inline.trim().replace(/^(<br>\s*)+|(\s*<br>)+$/g, '');
    this.inline = '';
    if (!isBlank(inner)) this.blocks.push(`<p>${inner}</p>`);
  }
}

function flow(children: readonly HtmlNode[], f: Flow, s: RenderState): void {
  for (const c of children) {
    if (c.type === 'text') {
      f.addInline(escapeHtml(c.text));
      continue;
    }
    switch (classify(c.name, s)) {
      case 'drop':
        break;
      case 'media':
        mediaFrom(c, s);
        break;
      case 'unwrap':
        flow(c.children, f, s);
        break;
      case 'break':
        f.flush();
        flow(c.children, f, s);
        f.flush();
        break;
      case 'inline':
        f.addInline(renderElement(c, s));
        break;
      case 'hr':
      case 'block':
        f.addBlock(renderElement(c, s));
        break;
    }
  }
}

/** The allow-list rebuild of an HTML fragment (engine.md §5). */
export function sanitiseHtml(html: string, opts: SanitiseOptions): SanitisedHtml {
  const s: RenderState = { opts, plain: opts.format === 'plain', media: [], firstImage: null };
  const f = new Flow();
  flow(parseTree(html), f, s);
  f.flush();
  return { blocks: f.blocks, media: s.media, firstImage: s.firstImage };
}

// ── Plain text → HTML ──────────────────────────────────────────────────────────────────────────

const URL_IN_TEXT = /https?:\/\/[^\s<>"']+/g;
const TRAILING_PUNCTUATION = /[.,;:!?)\]}'"»”’]+$/;

function linkify(line: string): string {
  let out = '';
  let last = 0;
  for (const m of line.matchAll(URL_IN_TEXT)) {
    const start = m.index ?? 0;
    let url = m[0];
    const trail = TRAILING_PUNCTUATION.exec(url);
    if (trail) url = url.slice(0, url.length - trail[0].length);
    out += escapeHtml(line.slice(last, start));
    const href = resolveHttpUrl(url, null);
    out += href ? `<a href="${escapeAttr(href)}">${escapeHtml(url)}</a>` : escapeHtml(url);
    last = start + url.length;
  }
  return out + escapeHtml(line.slice(last));
}

/** `text` only: blank-line separated paragraphs, single newlines as <br>, URLs auto-linked, all escaped. */
export function textToHtml(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .split(/\n[^\S\n]*\n\s*/)
    .map((para) => para.trim())
    .filter((para) => para !== '')
    .map((para) => `<p>${para.split('\n').map((line) => linkify(line.trim())).join('<br>')}</p>`)
    .join('');
}

// ── Truncation, excerpt, reading time ──────────────────────────────────────────────────────────

const ENCODER = new TextEncoder();
export const utf8Bytes = (s: string): number => ENCODER.encode(s).length;

/** Appended to a body cut at a block boundary. */
export const TRUNCATION_MARK = '<p>…</p>';

/** Join blocks, cutting at the last block boundary under `maxBytes` (engine.md §5). */
export function joinWithinBytes(blocks: readonly string[], maxBytes: number): { html: string; truncated: boolean } {
  const whole = blocks.join('');
  if (utf8Bytes(whole) <= maxBytes) return { html: whole, truncated: false };
  const budget = maxBytes - utf8Bytes(TRUNCATION_MARK);
  let used = 0;
  let html = '';
  for (const block of blocks) {
    const size = utf8Bytes(block);
    if (used + size > budget) break;
    html += block;
    used += size;
  }
  if (html === '') {
    // A single block larger than the cap: keep its text, cut by characters until it fits.
    let text = htmlToText(blocks[0] ?? '');
    let p = `<p>${escapeHtml(text)}</p>`;
    while (text.length > 0 && utf8Bytes(p) > budget) {
      text = text.slice(0, Math.floor(text.length * 0.9));
      p = `<p>${escapeHtml(text)}</p>`;
    }
    html = text === '' ? '' : p;
  }
  return { html: html + TRUNCATION_MARK, truncated: true };
}

/** First `maxChars` characters of the text, whitespace collapsed, cut back to a word boundary. */
export function makeExcerpt(text: string, maxChars: number): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  const chars = Array.from(flat);
  if (chars.length <= maxChars) return flat;
  const cut = chars.slice(0, maxChars).join('');
  // The next character is not a space: we are inside a word, cut back to the previous space.
  if (chars[maxChars] !== ' ') {
    const space = cut.lastIndexOf(' ');
    if (space > 0) return cut.slice(0, space).trimEnd();
  }
  return cut.trimEnd();
}

export function readingMinutes(wordCount: number, wordsPerMinute: number): number {
  return Math.max(1, Math.round(wordCount / wordsPerMinute));
}

export function normaliseTitle(title: string | undefined, maxChars: number): string | null {
  if (title === undefined) return null;
  const flat = title.replace(/\s+/g, ' ').trim();
  if (flat === '') return null;
  const chars = Array.from(flat);
  return chars.length <= maxChars ? flat : chars.slice(0, maxChars).join('').trimEnd();
}

// ── The whole item ─────────────────────────────────────────────────────────────────────────────

export interface NormaliseConfig {
  format: SourceFormat;
  keepImages: boolean;
  keepVideos: boolean;
  excerptChars: number;
  bodyMaxBytes: number;
  titleMaxChars: number;
  wordsPerMinute: number;
}

/** The content columns ingest writes (NewMessage minus identity and placement). */
export interface NormalisedContent {
  url: string | null;
  title: string | null;
  author: string | null;
  publishedAt: number | null;
  bodyHtml: string | null;
  excerpt: string;
  imageUrl: string | null;
  media: MediaItem[];
  wordCount: number;
  readingMinutes: number;
  lang: string | null;
  threadKey: string | null;
  extra: Record<string, unknown> | null;
}

function keptMedia(items: readonly MediaItem[] | undefined, keepImages: boolean, keepVideos: boolean): MediaItem[] {
  const out: MediaItem[] = [];
  for (const m of items ?? []) {
    if ((m.type === 'video' || m.type === 'audio') && !keepVideos) continue;
    if (m.type === 'image' && !keepImages) continue;
    const url = resolveHttpUrl(m.url, null);
    if (!url || out.some((o) => o.url === url)) continue;
    const item: MediaItem = { ...m, url };
    if (m.thumbnailUrl !== undefined) {
      const thumb = resolveHttpUrl(m.thumbnailUrl, null);
      if (thumb) item.thumbnailUrl = thumb;
      else delete item.thumbnailUrl;
    }
    out.push(item);
  }
  return out;
}

/** Normalise one raw item (engine.md §5). Pure: the reader-mode fetch happens before, in ingest. */
export function normaliseItem(raw: RawItem, cfg: NormaliseConfig): NormalisedContent {
  const url = resolveHttpUrl(raw.url, null);
  const source = raw.html !== undefined && raw.html !== '' ? raw.html : raw.text !== undefined && raw.text !== '' ? textToHtml(raw.text) : '';
  const sanitised = sanitiseHtml(source, { baseUrl: url, format: cfg.format, keepImages: cfg.keepImages, keepVideos: cfg.keepVideos });
  const { html, truncated } = joinWithinBytes(sanitised.blocks, cfg.bodyMaxBytes);
  const bodyHtml = html === '' ? null : html;
  const text = htmlToText(bodyHtml);
  const wordCount = tokens(text).length;
  const media = keptMedia(raw.media, cfg.keepImages, cfg.keepVideos);
  for (const m of sanitised.media) if (!media.some((o) => o.url === m.url)) media.push(m);
  let extra: Record<string, unknown> | null = raw.extra ? { ...raw.extra } : null;
  if (truncated) extra = { ...(extra ?? {}), truncated: true };
  return {
    url,
    title: normaliseTitle(raw.title, cfg.titleMaxChars),
    author: raw.author === undefined || raw.author.trim() === '' ? null : raw.author.replace(/\s+/g, ' ').trim(),
    publishedAt: raw.publishedAt !== undefined && Number.isFinite(raw.publishedAt) ? Math.floor(raw.publishedAt) : null,
    bodyHtml,
    excerpt: makeExcerpt(text, cfg.excerptChars),
    imageUrl: cfg.keepImages ? resolveHttpUrl(raw.imageUrl, url) ?? sanitised.firstImage : null,
    media,
    wordCount,
    readingMinutes: readingMinutes(wordCount, cfg.wordsPerMinute),
    lang: raw.lang ?? null,
    threadKey: raw.threadKey ?? null,
    extra,
  };
}
